import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight, ArrowUpRight, ChartLineUp, ShieldCheck, Compass, Buildings,
  MoonStars, Sun, List as Hamburger, X, FileText, GlobeHemisphereWest,
  Cpu, Check, Database, Stack, Scales, Pulse, MapPin, Envelope, Lightning,
  Atom, CurrencyDollar, TrendUp, Bank, Globe, Handshake, LinkedinLogo,
  Books, MapTrifold, Users, Briefcase, Bookmark, Rocket, ShieldStar,
  Sparkle, Money, Certificate, CaretDown, Notebook, Path, GraduationCap,
} from "@phosphor-icons/react";
import { useTheme } from "@/lib/theme";
import React, { useEffect, useState } from "react";
import Logo from "@/components/Logo";
import { useFlag } from "@/lib/flags";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";

/* ============================================================
   ORIXA INVEST — SaaS Marketing Site (V6.2)
   Positioning: "Market Intelligence Software for Emerging Markets."
   Product: Subscription SaaS platform (monthly / annual).
   ============================================================ */

const TRUST_CARDS = [
  { i: Cpu,          t: "AI Research Platform",         d: "Generate structured research assessments powered by advanced AI, with country, sector and risk depth — inside the software." },
  { i: Scales,       t: "Regulatory Intelligence",      d: "Access country-specific legal and regulatory requirements — licensing, incentives, compliance pathways." },
  { i: ShieldCheck,  t: "Risk Intelligence Platform",   d: "Understand political, operational, currency, execution and reputational risk in one integrated software view." },
  { i: FileText,     t: "Digital Investment Reports",   d: "Download boardroom-ready PDF · DOCX · PPTX dossiers directly from your subscription workspace." },
  { i: Compass,      t: "Decision Support Platform",    d: "Receive executable plans instead of raw data — sequenced actions, milestones and decision gates." },
  { i: GlobeHemisphereWest, t: "Emerging Markets Expertise", d: "Designed specifically for high-growth frontier and emerging economies — not adapted from OECD frameworks." },
];

const AUDIENCE_CARDS = [
  { i: Bank,      t: "Family Offices",                  d: "Multi-generational capital deployment across frontier and emerging markets." },
  { i: TrendUp,   t: "Private Equity",                  d: "Deal origination, due diligence and portfolio intelligence for EM-focused funds." },
  { i: ChartLineUp, t: "Investment Funds",              d: "Institutional research, thesis validation and cross-border structuring." },
  { i: ShieldStar, t: "Development Finance Institutions", d: "IFC · AfDB · Proparco · FMO · DEG · BII — mandate origination and monitoring." },
  { i: Buildings, t: "Corporates",                      d: "Strategic expansion, market entry and cross-border M&A in EM regions." },
  { i: Certificate, t: "Governments",                   d: "Investment attraction, sector prioritisation and diaspora capital mobilisation." },
  { i: GraduationCap, t: "Export Agencies",             d: "Trade promotion, market intelligence and buyer identification workflows." },
  { i: Briefcase, t: "Strategic Investors",             d: "Sovereign, pension and corporate allocators building long-term EM exposure." },
];

const MODULES = [
  { t: "Investment Intelligence",    status: "live" },
  { t: "Regulatory Intelligence",    status: "live" },
  { t: "Country Intelligence",       status: "live" },
  { t: "Market Intelligence",        status: "live" },
  { t: "Risk Intelligence",          status: "live" },
  { t: "Financial Intelligence",     status: "live" },
  { t: "Strategic Roadmaps",         status: "live" },
  { t: "Investment Documents",       status: "live" },
  { t: "Portfolio Intelligence",     status: "beta" },
  { t: "Partner Marketplace",        status: "soon" },
  { t: "Vehicle Import Intelligence", status: "soon" },
  { t: "API Platform",               status: "soon" },
];

const ROADMAP_RELEASED = [
  "Investment Intelligence",
  "Regulatory Intelligence",
  "Risk Engine",
  "PDF Export",
  "Billing Portal",
  "Feature Gating",
];

const ROADMAP_IN_DEV = [
  "Portfolio Management",
  "Investment Pipeline",
  "Partner Marketplace",
  "Vehicle Intelligence",
  "Institutional API",
];

const FAQS = [
  {
    q: "What is Orixa Invest?",
    a: "Orixa Invest is a subscription SaaS platform for AI-powered market intelligence in Emerging Markets. Subscribers access country intelligence, regulatory analysis, risk intelligence and investment research — all inside the software. It is not a consulting service.",
  },
  {
    q: "Who is it built for?",
    a: "Family offices, private equity funds, institutional investment teams, development finance institutions, corporates expanding across borders, governments, export agencies and strategic investors deploying capital in Africa, Middle East, India, ASEAN, Central Asia and Latin America.",
  },
  {
    q: "How are reports generated?",
    a: "Every assessment is generated inside the platform by a multi-agent AI pipeline that reasons over macroeconomic, corporate, regulatory and alternative data — then produces structured digital deliverables (memos, dossiers, roadmaps) that subscribers download as PDF, DOCX or PPTX. All outputs include confidence scoring and source references.",
  },
  {
    q: "Which countries are supported?",
    a: "The platform covers six Emerging-Market regions — Africa, Middle East, India, ASEAN, Central Asia and Latin America — spanning ten sector verticals from financial services to energy, agribusiness, logistics and healthcare.",
  },
  {
    q: "How is pricing structured?",
    a: "Four software plans: Explorer (free) for evaluation, Starter (25 000 FCFA / mois) for individual professionals, Professional (85 000 FCFA / mois) for investment teams, and Business (265 000 FCFA / mois) for larger organisations. Every plan is a self-serve monthly or annual subscription — no consulting engagement, no bespoke missions.",
  },
  {
    q: "How secure is my data?",
    a: "All workspaces are isolated per organisation with role-based access controls. Data at rest is encrypted; transport uses TLS 1.3. We do not train foundation models on customer content. Enterprise deployments support private data residency and audit-log integration.",
  },
];

/* ==========================================================
   Landing page
   ========================================================== */
export default function Landing() {
  const { theme, toggle } = useTheme();
  const [menu, setMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const subsFlag = useFlag("subscriptions");
  const isWaitlist = subsFlag.is("waitlist", "disabled");

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="landing-page">
      {/* JSON-LD FAQ schema (server-side crawlable) */}
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQS.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />

      {/* ============ TOP BAR ============ */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur-xl" role="banner">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo variant="wordmark" size={34} to="/" testId="landing-logo" />
          <nav className="hidden md:flex items-center gap-7 text-sm text-muted-foreground font-medium" aria-label="Primary">
            <a href="#platform" className="hover:text-foreground transition-colors" data-testid="nav-platform">Platform</a>
            <a href="#modules" className="hover:text-foreground transition-colors" data-testid="nav-modules">Modules</a>
            <a href="#audience" className="hover:text-foreground transition-colors" data-testid="nav-audience">Who we serve</a>
            <a href="#roadmap" className="hover:text-foreground transition-colors" data-testid="nav-roadmap">Roadmap</a>
            <a href="#pricing" className="hover:text-foreground transition-colors" data-testid="nav-pricing-anchor">Pricing</a>
            <a href="#faq" className="hover:text-foreground transition-colors" data-testid="nav-faq">FAQ</a>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={toggle} className="text-muted-foreground hover:text-foreground p-1.5" data-testid="landing-theme-toggle" aria-label="Toggle color theme">
              {theme === "dark" ? <Sun size={18} /> : <MoonStars size={18} />}
            </button>
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground font-medium" data-testid="landing-login">Sign in</Link>
            <Link to="/request-access" className="text-sm bg-primary text-primary-foreground rounded-lg px-4 py-2 font-semibold hover:bg-primary/90 transition-colors" data-testid="landing-signup">Start an assessment</Link>
          </div>
          <button className="md:hidden p-1.5" onClick={() => setMenu(!menu)} aria-label="Open menu" data-testid="landing-menu-toggle">
            {menu ? <X size={22} /> : <Hamburger size={22} />}
          </button>
        </div>
        {menu && (
          <div className="md:hidden border-t border-border px-4 py-4 space-y-3 bg-background" data-testid="landing-mobile-menu">
            <a href="#platform" onClick={() => setMenu(false)} className="block text-sm">Platform</a>
            <a href="#modules" onClick={() => setMenu(false)} className="block text-sm">Modules</a>
            <a href="#audience" onClick={() => setMenu(false)} className="block text-sm">Who we serve</a>
            <a href="#roadmap" onClick={() => setMenu(false)} className="block text-sm">Roadmap</a>
            <a href="#pricing" onClick={() => setMenu(false)} className="block text-sm">Pricing</a>
            <a href="#faq" onClick={() => setMenu(false)} className="block text-sm">FAQ</a>
            <div className="flex gap-2 pt-3">
              <Link to="/login" className="flex-1 text-center border border-border rounded-lg py-2 text-sm">Sign in</Link>
              <Link to="/request-access" className="flex-1 text-center bg-primary text-primary-foreground rounded-lg py-2 text-sm font-semibold">Start an assessment</Link>
            </div>
          </div>
        )}
      </header>

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden africa-bg border-b border-border" aria-labelledby="hero-title">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28 lg:py-36">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-card border border-border text-xs font-semibold uppercase tracking-[0.18em] px-3 py-1.5 rounded-full mb-8" data-testid="hero-eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-teal animate-pulse" />
              <span className="text-foreground">Market Intelligence Software · SaaS · Emerging Markets</span>
            </div>
            <h1 id="hero-title" className="font-display text-4xl sm:text-6xl lg:text-[5.5rem] tracking-tight leading-[0.98] font-normal" data-testid="hero-title">
              Institutional Intelligence<br />
              for <span className="italic text-primary">Emerging Markets.</span>
            </h1>
            <p className="mt-8 sm:mt-10 text-base lg:text-xl text-muted-foreground max-w-3xl leading-relaxed" data-testid="hero-subtitle">
              Orixa Invest is a subscription-based AI software platform delivering automated emerging market intelligence — country data, regulatory analysis, investment research and risk insights, all inside your workspace.
            </p>
            <div className="mt-10 sm:mt-12 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to={isWaitlist ? "/waitlist" : "/request-access"} className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-6 py-3.5 font-semibold hover:bg-primary/90 transition-colors card-elev" data-testid="hero-cta-primary">
                {isWaitlist ? "Join Early Access" : "Start an Assessment"} <ArrowRight weight="bold" />
              </Link>
              <a href="#pricing" className="inline-flex items-center justify-center gap-2 border border-border bg-card rounded-lg px-6 py-3.5 font-semibold hover:bg-secondary transition-colors" data-testid="hero-cta-secondary">
                See software plans
              </a>
            </div>
            {isWaitlist && (
              <p className="mt-5 text-xs text-muted-foreground max-w-md" data-testid="hero-waitlist-note">
                Early Access is currently open · Priority activation & founder-tier pricing locked for 12 months when subscriptions become fully operational.
              </p>
            )}

            {/* Trust ribbon */}
            <div className="mt-14 flex flex-wrap gap-2.5" data-testid="hero-trust-pills">
              {[
                { i: GlobeHemisphereWest, t: "6 Emerging-Market regions" },
                { i: Buildings, t: "10 institutional sectors" },
                { i: Cpu, t: "AI research platform" },
                { i: ShieldCheck, t: "Subscription SaaS" },
              ].map((p) => (
                <div key={p.t} className="inline-flex items-center gap-2 bg-card border border-border rounded-full px-3.5 py-2 text-xs sm:text-sm font-medium">
                  <p.i size={14} weight="bold" className="text-primary" />
                  {p.t}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ WHY INSTITUTIONS ============ */}
      <section id="platform" className="border-b border-border" aria-labelledby="trust-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-3xl">
            <div className="text-overline text-primary mb-4">01 / Platform</div>
            <h2 id="trust-title" className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Why institutions<br /><span className="italic text-muted-foreground">choose Orixa.</span>
            </h2>
            <p className="mt-5 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Six intelligence layers, one execution surface. Purpose-built for institutional decision-making in high-growth, high-complexity markets.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {TRUST_CARDS.map((c, i) => (
              <article key={c.t} className="bg-card border border-border rounded-2xl p-6 sm:p-7 flex flex-col hover:card-elev transition-shadow" data-testid={`trust-card-${i}`}>
                <div className="h-11 w-11 rounded-lg grid place-items-center mb-6 bg-teal-soft text-teal">
                  <c.i size={22} weight="duotone" />
                </div>
                <h3 className="font-display text-xl sm:text-2xl tracking-tight leading-tight mb-2.5">{c.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{c.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS (SaaS workflow) ============ */}
      <section id="how-it-works" className="border-b border-border" aria-labelledby="how-it-works-title" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-3xl">
            <div className="text-overline text-primary mb-4">02 / How it works</div>
            <h2 id="how-it-works-title" className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Software workflow<br /><span className="italic text-muted-foreground">from country to committee.</span>
            </h2>
            <p className="mt-5 text-sm sm:text-base text-muted-foreground leading-relaxed">
              Orixa Invest is a subscription platform. Every step below runs inside the product — no offline missions, no bespoke consulting.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5" role="list">
            {[
              { n: "01", i: GlobeHemisphereWest, t: "Choose a country", d: "Select a target Emerging Market from our covered universe. Country intelligence loads instantly." },
              { n: "02", i: Cpu,                  t: "Generate an AI-powered assessment", d: "Our institutional AI agents build a full research pack — market, regulatory, risk and financial." },
              { n: "03", i: FileText,             t: "Download your institutional report", d: "Export PDF, DOCX and PPTX dossiers ready for investment committees and boards." },
              { n: "04", i: Users,                t: "Collaborate with your team", d: "Share workspaces, comment on dossiers, and iterate — all within the subscription platform." },
            ].map((s, i) => (
              <article
                key={s.t}
                role="listitem"
                data-testid={`how-step-${i + 1}`}
                className="relative bg-card border border-border rounded-2xl p-6 sm:p-7 hover:card-elev transition-shadow"
              >
                <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-primary mb-4">Step {s.n}</div>
                <div className="h-11 w-11 rounded-lg grid place-items-center mb-6 bg-primary/10 text-primary">
                  <s.i size={22} weight="duotone" />
                </div>
                <h3 className="font-display text-lg sm:text-xl tracking-tight leading-tight mb-2.5">{s.t}</h3>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">{s.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WHO WE SERVE ============ */}
      <section id="audience" className="border-b border-border bg-secondary" aria-labelledby="audience-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-3xl">
            <div className="text-overline text-primary mb-4">02 / Designed for</div>
            <h2 id="audience-title" className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Built for the institutions<br /><span className="italic text-muted-foreground">shaping Emerging Markets.</span>
            </h2>
          </div>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {AUDIENCE_CARDS.map((a, i) => (
              <article key={a.t} className="bg-card border border-border rounded-2xl p-6 hover:card-elev transition-shadow" data-testid={`audience-card-${i}`}>
                <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary grid place-items-center mb-5">
                  <a.i size={20} weight="duotone" />
                </div>
                <h3 className="font-display text-lg tracking-tight mb-1.5">{a.t}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{a.d}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ============ INSTITUTIONAL MODULES ============ */}
      <section id="modules" className="border-b border-border" aria-labelledby="modules-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
            <div className="max-w-3xl">
              <div className="text-overline text-primary mb-4">03 / Product ecosystem</div>
              <h2 id="modules-title" className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Institutional Modules.<br />
                <span className="italic text-muted-foreground">One coherent surface.</span>
              </h2>
            </div>
            <Link to="/request-access" className="hidden lg:inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline" data-testid="modules-cta">
              Start using the platform <ArrowUpRight weight="bold" />
            </Link>
          </div>

          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border rounded-lg overflow-hidden" data-testid="modules-grid">
            {MODULES.map((m, i) => (
              <li key={m.t} className="bg-background hover:bg-card transition-colors p-5 sm:p-6 flex items-center justify-between gap-3" data-testid={`module-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-md bg-primary/5 text-primary grid place-items-center flex-shrink-0">
                    <Sparkle size={14} weight="duotone" />
                  </div>
                  <span className="font-semibold text-sm">{m.t}</span>
                </div>
                {m.status === "live" && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-teal-soft text-teal">Live</span>
                )}
                {m.status === "beta" && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-blue-soft text-blue">Beta</span>
                )}
                {m.status === "soon" && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full bg-secondary text-muted-foreground border border-border">Coming soon</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ============ PUBLIC ROADMAP ============ */}
      <section id="roadmap" className="border-b border-border bg-primary text-primary-foreground" aria-labelledby="roadmap-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-3xl">
            <div className="text-[10px] uppercase tracking-[0.22em] font-bold opacity-80 mb-4">04 / Public roadmap</div>
            <h2 id="roadmap-title" className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              A product built<br /><span className="italic opacity-80">in the open.</span>
            </h2>
            <p className="mt-5 text-sm sm:text-base opacity-80 max-w-2xl leading-relaxed">
              Transparency on what is live today, what is coming next, and what we are exploring. Institutional users have visibility on the entire product journey.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Released */}
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 sm:p-8" data-testid="roadmap-released">
              <div className="flex items-center gap-2.5 mb-6">
                <div className="h-8 w-8 rounded-md bg-teal-soft/20 text-teal grid place-items-center">
                  <Check size={14} weight="bold" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">Shipped</div>
                  <div className="font-display text-xl tracking-tight">Released</div>
                </div>
              </div>
              <ul className="space-y-2.5">
                {ROADMAP_RELEASED.map((r) => (
                  <li key={r} className="flex items-center gap-2.5 text-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-teal flex-shrink-0" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>

            {/* In Development */}
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 sm:p-8" data-testid="roadmap-in-dev">
              <div className="flex items-center gap-2.5 mb-6">
                <div className="h-8 w-8 rounded-md bg-gold-soft/20 text-gold grid place-items-center">
                  <Rocket size={14} weight="bold" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">Next</div>
                  <div className="font-display text-xl tracking-tight">In Development</div>
                </div>
              </div>
              <ul className="space-y-2.5">
                {ROADMAP_IN_DEV.map((r) => (
                  <li key={r} className="flex items-center gap-2.5 text-sm">
                    <span className="h-1.5 w-1.5 rounded-full bg-gold flex-shrink-0" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COVERAGE (kept from previous — institutional signal) ============ */}
      <section id="coverage" className="border-b border-border" aria-labelledby="coverage-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="text-overline text-primary mb-4">05 / Coverage</div>
          <h2 id="coverage-title" className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] max-w-3xl">
            Six Emerging-Market regions.<br /><span className="italic text-muted-foreground">Ten institutional sectors.</span>
          </h2>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14">
            <div>
              <div className="text-overline text-muted-foreground mb-4">Regions</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {["Africa","Middle East","India","ASEAN","Central Asia","Latin America"].map((r) => (
                  <div key={r} className="bg-card border border-border rounded-lg px-3 py-3 flex items-center gap-2.5">
                    <GlobeHemisphereWest size={16} weight="bold" className="text-primary flex-shrink-0" />
                    <span className="text-sm font-semibold">{r}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="text-overline text-muted-foreground mb-4">Sectors</div>
              <div className="grid grid-cols-2 gap-2">
                {["Financial Services","Infrastructure","Energy","Agribusiness","Manufacturing","Real Estate","Technology","Trade Finance","Logistics","Healthcare"].map((s) => (
                  <div key={s} className="bg-card border border-border rounded-lg px-3 py-2.5 text-sm font-medium">{s}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SOCIAL PROOF ============ */}
      <section className="border-b border-border bg-card" aria-labelledby="proof-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="max-w-3xl">
            <div className="text-overline text-primary mb-4">06 / Trusted</div>
            <h2 id="proof-title" className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
              Trusted by organisations<br /><span className="italic text-muted-foreground">building across Emerging Markets.</span>
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { i: Buildings, t: "Designed for institutional workflows", d: "From origination to due diligence to portfolio monitoring — every step follows the same institutional rigour." },
              { i: Cpu,       t: "Built on modern AI infrastructure",    d: "Multi-agent reasoning, retrieval-augmented intelligence and continuous data validation across authoritative sources." },
              { i: Compass,   t: "Created for long-term decision making",d: "Strategic depth over quick answers — every deliverable is anchored to a decision the user is trying to make." },
            ].map((v, i) => (
              <div key={v.t} className="bg-background border border-border rounded-2xl p-7" data-testid={`proof-card-${i}`}>
                <v.i size={20} weight="duotone" className="text-primary mb-4" />
                <div className="font-display text-lg tracking-tight mb-2">{v.t}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{v.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 text-xs text-muted-foreground text-center">
            Orixa Invest by Ibn Miyeta Group · A platform by Ibn Miyeta Group · Institutional-grade methodology and data governance.
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <PricingSection />

      {/* ============ FAQ ============ */}
      <section id="faq" className="border-b border-border" aria-labelledby="faq-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="text-overline text-primary mb-4">08 / Frequently asked</div>
              <h2 id="faq-title" className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Answers for<br /><span className="italic text-muted-foreground">institutional users.</span>
              </h2>
              <p className="mt-5 text-sm sm:text-base text-muted-foreground leading-relaxed">
                Still have questions? <a href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest" target="_blank" rel="noopener noreferrer" className="text-primary underline">Book a product demo</a> with our enterprise team.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="divide-y divide-border border-y border-border" data-testid="faq-list">
                {FAQS.map((f, i) => {
                  const open = openFaq === i;
                  return (
                    <div key={f.q} className="py-1">
                      <button
                        type="button"
                        onClick={() => setOpenFaq(open ? -1 : i)}
                        className="w-full flex items-center justify-between gap-4 py-5 text-left hover:opacity-80 transition-opacity"
                        aria-expanded={open}
                        data-testid={`faq-q-${i}`}
                      >
                        <span className="font-display text-lg sm:text-xl tracking-tight">{f.q}</span>
                        <CaretDown size={18} weight="bold" className={`text-muted-foreground transition-transform flex-shrink-0 ${open ? "rotate-180" : ""}`} />
                      </button>
                      {open && (
                        <div className="pb-6 pr-8 text-sm text-muted-foreground leading-relaxed" data-testid={`faq-a-${i}`}>
                          {f.a}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="border-b border-border africa-bg" aria-labelledby="final-cta-title">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-32 text-center">
          <h2 id="final-cta-title" className="font-display text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Institutional Intelligence.<br /><span className="italic text-primary">Delivered.</span>
          </h2>
          <p className="mt-7 text-muted-foreground max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
            Open a workspace, generate an institutional dossier in under eight minutes, or book a live product demo with our team.
          </p>
          <div className="mt-11 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/request-access" className="w-full sm:w-auto bg-primary text-primary-foreground rounded-lg px-6 py-3.5 font-semibold card-elev hover:bg-primary/90 transition-colors" data-testid="cta-signup">Start an Assessment</Link>
            <a href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto border border-border bg-card rounded-lg px-6 py-3.5 font-semibold hover:bg-secondary transition-colors" data-testid="cta-book">Book a product demo</a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

/* ============================================================
   Pricing Section — Paystack-only (V8.1 catalog-driven)
   Renders 3 tiers × Monthly/Annual toggle from /api/catalog/products.
   Paddle wiring removed from the active execution path.
   ============================================================ */
export function PricingSection() {
  const subsFlag = useFlag("subscriptions");
  const isWaitlist = subsFlag.is("waitlist", "disabled");
  const navigate = useNavigate();
  const { user } = useAuth();

  const [interval, setInterval] = useState("month");
  const [catalog, setCatalog] = useState(null);
  const [openingSlug, setOpeningSlug] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get("/catalog/products")
      .then((r) => { if (mounted) setCatalog(r.data); })
      .catch(() => { if (mounted) setCatalog({ products: [], catalog_version: null }); });
    return () => { mounted = false; };
  }, []);

  // Build 3-tier grid from catalog subscription products.
  const tiers = React.useMemo(() => {
    const products = catalog?.products || [];
    const map = new Map();
    for (const p of products) {
      if (p.type !== "subscription" || (p.amount || 0) <= 0) continue;
      const [, key, ivl] = /^sub_([a-z]+)_(monthly|annual)$/.exec(p.slug) || [];
      if (!key) continue;
      const bucket = map.get(key) || { key, name: p.name.replace(/ · (Monthly|Annual)$/i, ""),
                                        highlighted: key === "professional",
                                        features: [], month: null, year: null };
      if (ivl === "monthly") bucket.month = p;
      if (ivl === "annual") bucket.year = p;
      // Merge entitlement summary as feature bullets
      if (p.entitlements && bucket.features.length === 0) {
        bucket.features = Object.entries(p.entitlements)
          .filter(([, v]) => v === true || (typeof v === "number" && v > 0))
          .slice(0, 6)
          .map(([k, v]) => (typeof v === "number" ? `${v} · ${k.replace(/_/g, " ")}` : k.replace(/_/g, " ")));
      }
      map.set(key, bucket);
    }
    return ["starter", "professional", "business"].map((k) => map.get(k)).filter(Boolean);
  }, [catalog]);

  const formatXOF = (amount, currency) => {
    if (typeof amount !== "number") return null;
    if ((currency || "XOF").toUpperCase() === "XOF") {
      // Display XOF as FCFA with French thousand separators; no decimals.
      return `${amount.toLocaleString("fr-FR")} FCFA`;
    }
    return `${amount.toLocaleString()} ${currency || ""}`.trim();
  };

  async function handleSubscribe(tier) {
    const product = interval === "year" ? tier.year : tier.month;
    if (!product) return;
    if (!user) {
      navigate(`/login?next=/pricing&plan=${tier.key}&interval=${interval}`);
      return;
    }
    setOpeningSlug(product.slug);
    try {
      const intent = await api.post("/billing/intents", {
        product_id: product.slug, email: user.email, source: "pricing-page",
      });
      const ref = intent.data.reference;
      try {
        const init = await api.post(`/billing/intents/${ref}/initialize`);
        const url = init?.data?.authorization_url;
        if (url) { window.location.href = url; return; }
        throw new Error("No checkout URL returned.");
      } catch (initErr) {
        const code = initErr?.response?.data?.detail?.code;
        // Structured, user-friendly degradation. No raw Cloudflare error.
        if (code === "paystack_not_configured" || code === "paystack_channel_error") {
          toast.error("Subscription checkout is being finalised for this plan. We'll open a manual channel for you.");
          navigate(`/request-access?plan=${tier.key}&interval=${interval}`);
          return;
        }
        throw initErr;
      }
    } catch (e) {
      const msg = e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || e?.message
        || "Could not open checkout.";
      toast.error(typeof msg === "string" ? msg : "Could not open checkout.");
    } finally {
      setOpeningSlug(null);
    }
  }

  const loading = catalog === null;

  return (
    <section id="pricing" className="border-b border-border bg-secondary" aria-labelledby="pricing-title">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-28">
        <div className="text-overline text-primary mb-4">07 / Software Plans</div>
        <h2 id="pricing-title" className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] max-w-3xl">
          Subscription plans.<br /><span className="italic text-muted-foreground">Software access — monthly or annual.</span>
        </h2>
        <p className="mt-5 text-sm sm:text-base text-muted-foreground max-w-2xl">
          Every plan is a self-serve software subscription. Instant activation, cancel any time. No consulting, no bespoke missions — you subscribe to the platform and use it.
        </p>

        <div className="mt-8 inline-flex items-center gap-2 bg-card border border-border rounded-full p-1 shadow-sm" data-testid="pricing-interval-toggle" role="tablist" aria-label="Billing interval">
          {[
            { k: "month", label: "Monthly" },
            { k: "year",  label: "Annual", badge: "Save ~2 months" },
          ].map((opt) => (
            <button key={opt.k} type="button" role="tab"
              aria-selected={interval === opt.k}
              onClick={() => setInterval(opt.k)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${interval === opt.k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid={`pricing-interval-${opt.k}`}>
              {opt.label}
              {opt.badge && interval !== opt.k && (
                <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-teal">{opt.badge}</span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {loading && [1, 2, 3].map((i) => (
            <div key={i} className="bg-card border border-border rounded-2xl p-6 sm:p-7 animate-pulse min-h-[360px]" />
          ))}
          {!loading && tiers.length === 0 && (
            <div className="col-span-3 text-sm text-muted-foreground text-center py-8" data-testid="pricing-empty">
              Plans are currently being finalised. <Link to="/request-access" className="underline">Request access</Link> and we will notify you.
            </div>
          )}
          {!loading && tiers.map((t) => {
            const product = interval === "year" ? t.year : t.month;
            const displayFormatted = product ? formatXOF(product.amount, product.currency) : null;
            const cadenceLabel = interval === "year" ? "year" : "month";
            const testid = `tier-${t.key}`;
            const highlighted = t.highlighted;
            const paystackReady = !!product?.is_configured?.paystack;
            return (
              <div key={t.key}
                className={`relative bg-card border rounded-2xl p-6 sm:p-7 flex flex-col card-elev transition-all hover:card-elev-lg hover:-translate-y-0.5 ${highlighted ? "border-primary ring-2 ring-primary/20" : "border-border"}`}
                data-testid={testid} data-tier={t.key} data-interval={interval}
                data-product-slug={product?.slug || ""}
                data-server-amount={product?.amount ?? ""}
                data-server-currency={product?.currency ?? ""}>
                {highlighted && (
                  <div className="absolute -top-3 left-6 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-[0.18em] px-3 py-1 rounded-full">Recommended</div>
                )}
                <div className="text-overline text-muted-foreground mb-3" data-testid={`${testid}-name`}>{t.name}</div>
                <div className="font-display text-4xl tracking-tight text-foreground" data-testid={`${testid}-price`}>
                  {displayFormatted ? (
                    <>
                      {displayFormatted}
                      <span className="text-sm text-muted-foreground font-sans font-normal" data-testid={`${testid}-cadence`}> / {cadenceLabel}</span>
                    </>
                  ) : (
                    <span className="text-muted-foreground text-2xl">—</span>
                  )}
                </div>
                <div className="mt-1 text-xs text-muted-foreground" data-testid={`${testid}-billing-frequency`}>
                  Billed {interval === "year" ? "annually" : "monthly"} · recurring subscription
                </div>
                <ul className="mt-5 space-y-2.5 text-sm flex-1" data-testid={`${testid}-features`}>
                  {(t.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2.5">
                      <div className="h-5 w-5 rounded-full bg-teal-soft text-teal grid place-items-center flex-shrink-0 mt-0.5"><Check size={11} weight="bold" /></div>
                      <span className="leading-snug capitalize">{f}</span>
                    </li>
                  ))}
                </ul>

                {isWaitlist ? (
                  <Link to={`/waitlist?plan=${t.key}`} className={`mt-8 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold transition-colors ${highlighted ? "bg-primary text-primary-foreground hover:bg-primary/90 card-elev" : "bg-foreground text-background hover:bg-foreground/90"}`} data-testid={`${testid}-cta`}>
                    Join Early Access <ArrowRight weight="bold" />
                  </Link>
                ) : product && paystackReady ? (
                  <button type="button"
                    disabled={openingSlug === product.slug}
                    onClick={() => handleSubscribe(t)}
                    className={`mt-8 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${highlighted ? "bg-primary text-primary-foreground hover:bg-primary/90 card-elev" : "bg-foreground text-background hover:bg-foreground/90"}`}
                    data-testid={`${testid}-cta`}
                    data-product-slug={product.slug}>
                    {openingSlug === product.slug ? "Opening…" : "Subscribe"} <ArrowRight weight="bold" />
                  </button>
                ) : (
                  <Link to={`/request-access?plan=${t.key}${interval === "year" ? "&interval=year" : ""}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold transition-colors border border-border bg-card hover:bg-secondary`}
                    data-testid={`${testid}-cta`}>
                    Request access <ArrowRight weight="bold" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>

        <div className="mt-10 bg-card border border-border rounded-2xl p-5 sm:p-6" data-testid="pricing-disclosures">
          <div className="text-overline text-muted-foreground mb-3">Billing terms</div>
          <ul className="space-y-2.5 text-sm text-muted-foreground leading-relaxed">
            <li data-testid="disclosure-taxes">Taxes may apply and will be calculated at checkout.</li>
            <li data-testid="disclosure-autorenew">Subscriptions renew automatically until cancelled.</li>
            <li data-testid="disclosure-cancel">You can manage or cancel your subscription at any time from the Customer Portal.</li>
            <li data-testid="disclosure-no-trial">No free trials — all plans are paid subscriptions billed immediately at the price shown above.</li>
            <li data-testid="disclosure-mor">Payments are processed securely by Paystack on behalf of Ibn Miyeta Group.</li>
          </ul>
        </div>

        <div className="mt-6 text-xs text-muted-foreground text-center">
          Secure subscription payments powered by Paystack · Cards · Mobile Money · Bank Transfer · Enterprise via sales team
          {" · "}
          <Link to="/request-access?plan=enterprise" className="underline hover:text-foreground" data-testid="tier-enterprise-link">Talk to enterprise sales</Link>
        </div>
      </div>
    </section>
  );
}

/* ============================================================
   Premium Site Footer — exported (also used by /pricing, /methodology)
   ============================================================ */
export function SiteFooter() {
  return (
    <footer className="bg-card" role="contentinfo">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-2 md:grid-cols-6 gap-8 sm:gap-10 text-sm">
        {/* Brand */}
        <div className="col-span-2">
          <Logo variant="wordmark" size={34} as="div" className="mb-4" testId="landing-footer-logo" />
          <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mb-6">
            Market Intelligence Software for Emerging Markets. A subscription SaaS platform by Ibn Miyeta Group.
          </p>
          <div className="space-y-2 text-xs text-muted-foreground">
            <div className="font-semibold text-foreground">Orixa Invest by Ibn Miyeta Group · A platform by Ibn Miyeta Group</div>
            <div className="flex items-start gap-2"><MapPin size={14} className="mt-0.5 flex-shrink-0" /><span>Abidjan, Côte d&apos;Ivoire</span></div>
            <a href="mailto:contact@orixa-invest.co" className="flex items-center gap-2 hover:text-foreground transition-colors" data-testid="footer-email">
              <Envelope size={14} className="flex-shrink-0" /> contact@orixa-invest.co
            </a>
            <a href="https://www.ibnmiyetagroup.org" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-foreground transition-colors" data-testid="footer-linkedin">
              <LinkedinLogo size={14} className="flex-shrink-0" /> Ibn Miyeta Group
            </a>
          </div>
        </div>

        {/* About */}
        <div>
          <div className="text-overline text-muted-foreground mb-4 text-[10px]">About</div>
          <ul className="space-y-2.5 text-xs text-muted-foreground">
            <li><a href="/#platform" className="hover:text-foreground" data-testid="footer-about">Platform</a></li>
            <li><a href="/#audience" className="hover:text-foreground">Who we serve</a></li>
            <li><Link to="/methodology" className="hover:text-foreground">Methodology</Link></li>
            <li><Link to="/contact" className="hover:text-foreground" data-testid="footer-contact">Contact</Link></li>
          </ul>
        </div>

        {/* Platform */}
        <div>
          <div className="text-overline text-muted-foreground mb-4 text-[10px]">Platform</div>
          <ul className="space-y-2.5 text-xs text-muted-foreground">
            <li><a href="/#modules" className="hover:text-foreground">Modules</a></li>
            <li><a href="/#roadmap" className="hover:text-foreground" data-testid="footer-roadmap">Roadmap</a></li>
            <li><a href="/#faq" className="hover:text-foreground">FAQ</a></li>
            <li><Link to="/methodology" className="hover:text-foreground" data-testid="footer-docs">Documentation</Link></li>
          </ul>
        </div>

        {/* Product */}
        <div>
          <div className="text-overline text-muted-foreground mb-4 text-[10px]">Product</div>
          <ul className="space-y-2.5 text-xs text-muted-foreground">
            <li><a href="/#pricing" className="hover:text-foreground" data-testid="footer-pricing">Pricing</a></li>
            <li><Link to="/request-access" className="hover:text-foreground">Start an assessment</Link></li>
            <li><Link to="/login" className="hover:text-foreground">Sign in</Link></li>
            <li><a href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest" target="_blank" rel="noopener noreferrer" className="hover:text-foreground">Book a product demo</a></li>
          </ul>
        </div>

        {/* Legal */}
        <div>
          <div className="text-overline text-muted-foreground mb-4 text-[10px]">Legal</div>
          <ul className="space-y-2.5 text-xs text-muted-foreground">
            <li><Link to="/legal/terms" className="hover:text-foreground" data-testid="footer-terms">Terms of Service</Link></li>
            <li><Link to="/legal/privacy" className="hover:text-foreground" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/legal/refund" className="hover:text-foreground" data-testid="footer-refund">Refund Policy</Link></li>
            <li>
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("orixa:openCookies"))}
                className="hover:text-foreground text-left"
                data-testid="footer-cookie-preferences"
              >
                Cookie preferences
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-[11px] text-muted-foreground" data-testid="footer-legal-strip">
          <div>Copyright &copy; {new Date().getFullYear()} Orixa Invest by Ibn Miyeta Group. A platform by Ibn Miyeta Group.</div>
          <div className="max-w-xl leading-relaxed">Research and decision-support only. Not financial, legal, tax or investment advice.</div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6 text-[11px] text-muted-foreground" data-testid="footer-saas-disclosure">
          Orixa Invest is a subscription-based software platform developed by Ibn Miyeta Group.
        </div>
      </div>
    </footer>
  );
}
